import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useChatContext } from './AppDataContext';
import { AskUserAnswer, AskUserCardData, findPendingAskUserCard } from '../helpers/askUser';

/**
 * Answers an `ask_user` card: message id, card id, one answer per question.
 * Resolves true once the reply is resuming.
 */
export type AnswerQuestionHandler = (
  messageId: string,
  cardId: string,
  answers: AskUserAnswer[]
) => Promise<boolean>;

export interface ActiveQuestion {
  messageId: string;
  card: AskUserCardData;
}

interface AskUserController {
  answerQuestion: AnswerQuestionHandler;
  /** A pending card on the latest finished reply: the one the user can answer. */
  activeQuestion: ActiveQuestion | null;
  /** True while the active question replaces the message input. */
  isComposerOpen: boolean;
  /** Skip: bring the message input back. Sending a message then skips it for good. */
  dismiss: () => void;
  /** Open a skipped question in place of the input again. */
  reopen: () => void;
}

/** Null outside a live chat (shared views, resource panes): cards are read-only. */
const AskUserContext = createContext<AskUserController | null>(null);

export const AskUserProvider = ({
  answerQuestion,
  children,
}: {
  answerQuestion: AnswerQuestionHandler;
  children: React.ReactNode;
}) => {
  const { filteredMessages, currentChatId } = useChatContext();
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(() => new Set());

  // Skips are per chat view; a new chat starts clean.
  useEffect(() => {
    setDismissedIds(new Set());
  }, [currentChatId]);

  const activeQuestion = useMemo<ActiveQuestion | null>(() => {
    const last = filteredMessages[filteredMessages.length - 1];
    if (!last || !last.isAi || last.isGenerating) return null;
    const card = findPendingAskUserCard(last.content);
    return card ? { messageId: last._id, card } : null;
  }, [filteredMessages]);

  const activeId = activeQuestion?.card.id;
  const isComposerOpen = !!activeId && !dismissedIds.has(activeId);

  const dismiss = useCallback(() => {
    if (!activeId) return;
    setDismissedIds(prev => new Set(prev).add(activeId));
  }, [activeId]);

  const reopen = useCallback(() => {
    if (!activeId) return;
    setDismissedIds(prev => {
      const next = new Set(prev);
      next.delete(activeId);
      return next;
    });
  }, [activeId]);

  const value = useMemo(() => ({
    answerQuestion,
    activeQuestion,
    isComposerOpen,
    dismiss,
    reopen,
  }), [answerQuestion, activeQuestion, isComposerOpen, dismiss, reopen]);

  return <AskUserContext.Provider value={value}>{children}</AskUserContext.Provider>;
};

export const useAskUser = (): AskUserController | null => useContext(AskUserContext);
